<script setup lang="ts">
import { isJust, just, matchMaybe, nothing } from "../lib/maybe";
import { computed } from "vue";
import { useProjectGoalsStore } from "../stores/useProjectGoalsStore";
import {
  formatGoalProgress,
  goalTarget,
  parseGoalDraft,
} from "../lib/writingGoals";
import ZwButton from "./ui/ZwButton.vue";
import ZwInput from "./ui/ZwInput.vue";

const sessionWords = defineModel<string>("sessionWords", { required: true });
const dailyWords = defineModel<string>("dailyWords", { required: true });
const projectWords = defineModel<string>("projectWords", { required: true });

defineProps<{
  /** Bumped by the parent when the dialog opens, to remount these ZwInputs
   * with freshly seeded values. */
  fieldsKey: number;
}>();

const goalsStore = useProjectGoalsStore();
const goalsProgress = computed(() => goalsStore.goalsProgress);

function formatDraftProgress(progress: number, draft: string): string {
  return matchMaybe(goalTarget(parseGoalDraft(draft)), {
    nothing: () => "",
    just: (target) => formatGoalProgress(progress, target),
  });
}
</script>

<template>
  <section
    class="project-settings-group"
    aria-labelledby="settings-goals-heading"
  >
    <h3 id="settings-goals-heading" class="project-settings-subhead">
      Writing goals
    </h3>
    <p class="project-settings-hint">
      Set word-count goals for your manuscript. Notes and worldbuilding don’t
      count toward progress. Leave a target blank to turn it off.
    </p>

    <div :key="fieldsKey" class="project-settings-goals-fields">
      <div class="project-settings-goal-row">
        <ZwInput
          :id="nothing"
          v-model="sessionWords"
          :label="just('Session target')"
          placeholder="e.g. 2000"
          inputmode="numeric"
          autocomplete="off"
          spellcheck="false"
        />
        <ZwButton
          size="sm"
          type="button"
          :disabled="sessionWords.length === 0"
          @click="sessionWords = ''"
        >
          Clear
        </ZwButton>
      </div>
      <p class="project-settings-hint">
        Words added since this project was opened.
        <template v-if="isJust(goalTarget(parseGoalDraft(sessionWords)))">
          Progress:
          {{ formatDraftProgress(goalsProgress?.session ?? 0, sessionWords) }}
        </template>
      </p>

      <div class="project-settings-goal-row">
        <ZwInput
          :id="nothing"
          v-model="dailyWords"
          :label="just('Daily target')"
          placeholder="e.g. 1000"
          inputmode="numeric"
          autocomplete="off"
          spellcheck="false"
        />
        <ZwButton
          size="sm"
          type="button"
          :disabled="dailyWords.length === 0"
          @click="dailyWords = ''"
        >
          Clear
        </ZwButton>
      </div>
      <p class="project-settings-hint">
        Words added today (local midnight rollover).
        <template v-if="isJust(goalTarget(parseGoalDraft(dailyWords)))">
          Progress:
          {{ formatDraftProgress(goalsProgress?.daily ?? 0, dailyWords) }}
        </template>
      </p>

      <div class="project-settings-goal-row">
        <ZwInput
          :id="nothing"
          v-model="projectWords"
          :label="just('Project target')"
          placeholder="e.g. 80000"
          inputmode="numeric"
          autocomplete="off"
          spellcheck="false"
        />
        <ZwButton
          size="sm"
          type="button"
          :disabled="projectWords.length === 0"
          @click="projectWords = ''"
        >
          Clear
        </ZwButton>
      </div>
      <p class="project-settings-hint">
        Total manuscript words toward a finished draft.
        <template v-if="isJust(goalTarget(parseGoalDraft(projectWords)))">
          Progress:
          {{ formatDraftProgress(goalsProgress?.project ?? 0, projectWords) }}
        </template>
      </p>
    </div>
  </section>
</template>
